import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Camera } from 'lucide-react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/* Fortschrittsfotos (13.10.2026, gap.md Welle 2): Das Mitglied fotografiert sich in der App (vorne, seitlich, hinten),
   Coach und CRM vergleichen zwei Tage nebeneinander. Bilder kommen nur mit Anmeldung (verschlüsselt gespeichert) — deshalb
   als Blob geladen, nie als offene URL. Schalter Standard aus; eingeschaltet wird unten in der Schalter-Karte. */

const ANSICHT = { vorne: 'vorne', seite: 'seitlich', hinten: 'hinten' };
const datum = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

function Bild({ fid, bildLaden, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let weg = null;
    let aktiv = true;
    if (fid) {
      bildLaden(fid).then((r) => { if (aktiv) { weg = URL.createObjectURL(r.data); setUrl(weg); } }).catch(() => setUrl(null));
    }
    return () => { aktiv = false; if (weg) URL.revokeObjectURL(weg); };
  }, [fid, bildLaden]);
  if (!fid) return <div className="flex aspect-[3/4] items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">kein Foto</div>;
  if (!url) return <Skeleton className="aspect-[3/4] rounded-md" />;
  return <img src={url} alt={alt} className="aspect-[3/4] w-full rounded-md object-cover" />;
}

export default function Fortschrittsfotos({ laden, bildLaden, testId = 'fortschrittsfotos' }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [ansicht, setAnsicht] = useState('vorne');
  const [links, setLinks] = useState(null);
  const [rechts, setRechts] = useState(null);
  const lade = useCallback(() => {
    laden().then((r) => {
      setD(r.data); setErr('');
      const t = r.data.tage || [];
      if (t.length) { setRechts(t[0].datum); setLinks(t[t.length - 1].datum); }
    }).catch((e) => setErr(fehlerText(e, 'Fotos nicht geladen')));
  }, [laden]);
  useEffect(() => { lade(); }, [lade]);
  const tage = useMemo(() => Object.fromEntries((d?.tage || []).map((t) => [t.datum, t.fotos])), [d]);

  if (err) return null;
  if (!d) return <Skeleton className="h-24" />;
  if (!d.aktiv) return null;      // Schalter aus: nichts anzeigen (Hinweis steht in der Schalter-Karte)
  const auswahl = (wert, setzen, label) => (
    <select aria-label={label} className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={wert || ''} onChange={(e) => setzen(e.target.value)}>
      {d.tage.map((t) => <option key={t.datum} value={t.datum}>{datum(t.datum)}</option>)}
    </select>
  );
  return (
    <Card className="border-border/60" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><Camera className="h-4 w-4 text-primary" /> Fortschrittsfotos
          {!d.einwilligung?.erteilt && <Badge variant="outline" className="text-[10px]">keine Einwilligung</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Vom Mitglied in der App aufgenommen. Nur für Beratung – nicht weitergeben, nicht für Werbung.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {!d.tage.length && <p className="text-sm text-muted-foreground">{d.einwilligung?.erteilt ? 'Noch keine Fotos.' : 'Das Mitglied hat (noch) nicht eingewilligt.'}</p>}
        {d.tage.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {Object.entries(ANSICHT).map(([k, v]) => (
                <Button key={k} size="sm" variant={ansicht === k ? 'default' : 'outline'} onClick={() => setAnsicht(k)} aria-pressed={ansicht === k}>{v}</Button>
              ))}
              <span className="ml-auto flex items-center gap-1.5 text-sm">{auswahl(links, setLinks, 'Vorher')} <span aria-hidden="true">→</span> {auswahl(rechts, setRechts, 'Nachher')}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[links, rechts].map((tag, i) => (
                <figure key={`${i}-${tag}`} className="space-y-1">
                  <Bild fid={tage[tag]?.[ansicht]?.id} bildLaden={bildLaden} alt={`${ANSICHT[ansicht]} am ${tag}`} />
                  <figcaption className="text-center text-xs text-muted-foreground">{i ? 'Nachher' : 'Vorher'} · {tag ? datum(tag) : ''}</figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* CRM: Lead › Kraftlog */
export function FortschrittsfotosCrm({ leadId }) {
  const f = useMemo(() => ({
    laden: () => api.get(`/fortschrittsfotos/mitglied/${leadId}`),
    bildLaden: (fid) => api.get(`/fortschrittsfotos/mitglied/${leadId}/${fid}`, { params: { klein: false }, responseType: 'blob' }),
  }), [leadId]);
  return <Fortschrittsfotos key={leadId} {...f} />;
}

/* CRM: Schalter unter /planer — Text lesen, Fassung freigeben, einschalten (nur Leitung). */
export function FortschrittsfotosSchalter() {
  const [s, setS] = useState(null);
  const [auf, setAuf] = useState(false);
  useEffect(() => { if (auf) api.get('/fortschrittsfotos/schalter').then((r) => setS(r.data)).catch(() => setS(null)); }, [auf]);
  const setzen = async (body, ok) => {
    try { const r = await api.put('/fortschrittsfotos/schalter', body); setS(r.data); toast.success(ok); }
    catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
  };
  return (
    <Card className="border-border/60 print:hidden" data-testid="fortschrittsfotos-schalter">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Fortschrittsfotos</CardTitle>
          <p className="text-xs text-muted-foreground">Standard aus. Erst den Einwilligungstext prüfen (Anwalt) und freigeben, dann einschalten.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAuf((v) => !v)} aria-expanded={auf}>{auf ? 'Zuklappen' : 'Öffnen'}</Button>
      </CardHeader>
      {auf && s && (
        <CardContent className="space-y-3">
          <p className="text-sm">Stand: <b>{s.aktiv ? 'eingeschaltet' : 'aus'}</b>{s.freigegeben && ` · Text freigegeben von ${s.freigegeben.von}`}</p>
          {s.einwilligung.entwurf && <p className="text-xs font-semibold text-destructive">ENTWURF – nicht anwaltlich geprüft.</p>}
          <details><summary className="cursor-pointer text-sm font-semibold">{s.einwilligung.titel} ({s.einwilligung.version})</summary>
            <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{s.einwilligung.text}</p></details>
          <div className="flex flex-wrap gap-2">
            {!s.text_freigegeben && <Button size="sm" variant="outline" onClick={() => window.confirm('Genau diese Fassung freigeben?') && setzen({ text_freigeben_version: s.einwilligung.version }, 'Text freigegeben')}>Text freigeben</Button>}
            <Button size="sm" disabled={!s.text_freigegeben} onClick={() => setzen({ aktiv: !s.aktiv }, s.aktiv ? 'Ausgeschaltet' : 'Eingeschaltet')}>{s.aktiv ? 'Ausschalten' : 'Einschalten'}</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
